import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { twMerge } from "tailwind-merge";

import { ConfirmationDialog } from "ui/confirmation-dialog";
import toast from "components/partials/toast/toast";

import { useArchiveCampaignTemplate } from "hooks/api/use-campaign-templates";
import { campaignTemplateKeys } from "hooks/api/query-keys";
import { getErrorMessage } from "utilities";
import { getProgramTypeLabel } from "containers/admin/clients/client/campaign-templates-page/campaign-template.constants";

import type { CampaignTemplate } from "containers/admin/clients/client/campaign-templates-page/campaign-templates-page.types";

type UseArchiveCampaignTemplateModalProps = {
  onSuccess?: () => void;
  onRemoveSelectedItem?: (id: string) => void;
};

export const useArchiveCampaignTemplateModal = ({
  onSuccess,
  onRemoveSelectedItem,
}: UseArchiveCampaignTemplateModalProps = {}) => {
  const [templatesToArchive, setTemplatesToArchive] = useState<CampaignTemplate[]>([]);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const queryClient = useQueryClient();
  const archiveMutation = useArchiveCampaignTemplate();

  const openArchiveModal = useCallback((templates: CampaignTemplate | CampaignTemplate[]) => {
    const isBulk = Array.isArray(templates);
    const templatesArray = Array.isArray(templates) ? templates : [templates];

    setTemplatesToArchive(templatesArray);
    setIsBulkMode(isBulk);
  }, []);

  const closeArchiveModal = useCallback(() => {
    if (archiveMutation.isPending) {
      return;
    }

    setTemplatesToArchive([]);
    setIsBulkMode(false);
  }, [archiveMutation.isPending]);

  const handleArchive = useCallback(async () => {
    if (templatesToArchive.length === 0) {
      return;
    }

    if (!isBulkMode) {
      archiveMutation.mutate(templatesToArchive[0].id, {
        onSuccess: () => {
          toast.success({ data: { title: "Archived brief" } });
          onSuccess?.();
          closeArchiveModal();
        },
        onError: (err) => {
          toast.error({
            data: {
              title: "Failed to archive brief",
              message: getErrorMessage(err),
            },
          });
        },
      });

      return;
    }

    const results = await Promise.allSettled(
      templatesToArchive.map((template) =>
        archiveMutation.mutateAsync({ id: template.id, meta: { skipInvalidation: true } }),
      ),
    );

    const successIds: string[] = [];
    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        successIds.push(templatesToArchive[index].id);
      }
    });

    const successCount = successIds.length;
    const failCount = results.length - successCount;

    if (successCount) {
      queryClient.invalidateQueries({ queryKey: campaignTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: campaignTemplateKeys.summaries() });
      successIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: campaignTemplateKeys.detail(id) });
      });
      toast.success({
        data: { title: `Archived ${successCount} brief${successCount > 1 ? "s" : ""}` },
      });
      onSuccess?.();

      setTemplatesToArchive((prev) => prev.filter((t) => !successIds.includes(t.id)));
      successIds.forEach((id) => onRemoveSelectedItem?.(id));

      if (!failCount) {
        closeArchiveModal();
      }
    }

    if (failCount) {
      toast.error({
        data: {
          title: "Failed to archive some briefs",
          message: getErrorMessage(results.find((r) => r.status === "rejected")?.reason),
        },
      });
    }
  }, [
    archiveMutation,
    closeArchiveModal,
    isBulkMode,
    onRemoveSelectedItem,
    onSuccess,
    queryClient,
    templatesToArchive,
  ]);

  const archiveModalMarkup = useMemo(() => {
    if (templatesToArchive.length === 0) {
      return null;
    }

    const isPending = archiveMutation.isPending;
    const title = isBulkMode
      ? "Are you sure you want to archive these briefs?"
      : "Are you sure you want to archive this brief?";
    const confirmLabel = isBulkMode ? "Archive briefs" : "Archive brief";
    const hasScrollableList = templatesToArchive.length > 5;

    return (
      <ConfirmationDialog
        open
        onClose={closeArchiveModal}
        onConfirm={handleArchive}
        intent="error"
        headline={title}
        confirmButtonText={confirmLabel}
        confirmLoadingMessage="Archiving..."
        confirming={isPending}
        closable={!isPending}>
        {isBulkMode && (
          <div className="flex flex-col gap-2 mt-2">
            <h2>Selected item(s)</h2>
            <div
              className={twMerge(
                "flex flex-col gap-2",
                hasScrollableList && "max-h-[130px] overflow-y-auto custom-scroll",
              )}>
              {templatesToArchive.map((template) => (
                <div key={template.id} className="flex items-center gap-2 text-sm font-normal">
                  <span className="text-gray-900">{template.name}</span>
                  <span className="text-gray-500">{getProgramTypeLabel(template.program)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </ConfirmationDialog>
    );
  }, [archiveMutation.isPending, closeArchiveModal, handleArchive, isBulkMode, templatesToArchive]);

  return {
    openArchiveModal,
    archiveModalMarkup,
  };
};
